import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiService from '../../services/shared/apiService';
import { useAuth } from '../../context/shared/AuthContext';

interface MessageSchedulerProps {
  patientId?: string; // If provided, pre-selects this patient
}

interface ScheduledMessage {
  id: string;
  recipientId: string;
  recipientName: string;
  message: string;
  scheduledAt: string;
  sendAt: string;
  messageType: 'ai_generated' | 'custom';
  status: 'pending' | 'sent' | 'cancelled';
  createdAt: string;
}

const MessageScheduler: React.FC<MessageSchedulerProps> = ({ patientId }) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  const [selectedPatient, setSelectedPatient] = useState(patientId || '');
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState<'ai_generated' | 'custom'>('ai_generated');
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Fetch therapist's patients
  const { data: patients } = useQuery({
    queryKey: ['therapist-patients', user?.uid],
    queryFn: () => apiService.getTherapistPatients(user?.uid || ''),
    enabled: !!user?.uid,
  });

  // Fetch scheduled messages
  const { data: scheduledMessages } = useQuery({
    queryKey: ['scheduled-messages', user?.uid],
    queryFn: () => apiService.makeRequest(`/api/therapist/${user?.uid}/scheduled-messages`),
    enabled: !!user?.uid,
  });

  // Schedule message mutation
  const scheduleMessageMutation = useMutation({
    mutationFn: async (messageData: {
      recipientId: string;
      message: string;
      sendAt: string;
      messageType: 'ai_generated' | 'custom';
    }) => {
      return apiService.makeRequest(`/api/therapist/${user?.uid}/schedule-message`, {
        method: 'POST',
        data: messageData
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduled-messages', user?.uid] });
      setIsModalOpen(false);
      setMessage('');
      setScheduledDate('');
      setScheduledTime('');
      setSelectedPatient('');
    },
  });

  // Cancel message mutation
  const cancelMessageMutation = useMutation({
    mutationFn: async (messageId: string) => {
      return apiService.makeRequest(`/api/therapist/${user?.uid}/scheduled-messages/${messageId}/cancel`, {
        method: 'POST'
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduled-messages', user?.uid] });
    },
  });

  const handleScheduleMessage = () => {
    if (!selectedPatient || !message || !scheduledDate || !scheduledTime) {
      alert('Please fill in all fields');
      return;
    }

    const sendAt = new Date(`${scheduledDate}T${scheduledTime}`).toISOString();
    
    scheduleMessageMutation.mutate({
      recipientId: selectedPatient,
      message,
      sendAt,
      messageType
    });
  };

  const handleCancelMessage = (messageId: string) => {
    if (confirm('Are you sure you want to cancel this scheduled message?')) {
      cancelMessageMutation.mutate(messageId);
    }
  };

  const getPatientName = (patientId: string) => {
    return patients?.patients?.find((p: any) => p.id === patientId)?.displayName || 'Unknown Patient';
  };

  const formatDateTime = (dateTime: string) => {
    return new Date(dateTime).toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'sent': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <div className="text-2xl mr-3">📅</div>
          <h3 className="text-lg font-semibold">Message Scheduler</h3>
        </div>
        <button
          onClick={() => setIsModalOpen(true)}
          className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-sm transition-colors"
        >
          Schedule New Message
        </button>
      </div>

      {/* Scheduled Messages List */}
      <div className="space-y-4">
        <h4 className="font-medium">Scheduled Messages</h4>
        {scheduledMessages?.messages?.length > 0 ? (
          scheduledMessages.messages.map((msg: ScheduledMessage) => (
            <div key={msg.id} className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-2">
                  <span className="font-medium">{getPatientName(msg.recipientId)}</span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(msg.status)}`}>
                    {msg.status}
                  </span>
                </div>
                {msg.status === 'pending' && (
                  <button
                    onClick={() => handleCancelMessage(msg.id)}
                    className="text-red-500 hover:text-red-600 text-sm"
                  >
                    Cancel
                  </button>
                )}
              </div>
              
              <div className="text-sm text-gray-600  mb-2">
                <strong>Scheduled for:</strong> {formatDateTime(msg.sendAt)}
              </div>
              
              <div className="text-sm text-gray-600  mb-2">
                <strong>Type:</strong> {msg.messageType === 'ai_generated' ? 'AI Generated' : 'Custom'}
              </div>
              
              <div className="bg-gray-50  p-3 rounded-lg">
                <p className="text-sm">{msg.message}</p>
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-8">
            <div className="text-3xl mb-3">📝</div>
            <p className="text-gray-500 ">
              No scheduled messages. Schedule your first message to get started.
            </p>
          </div>
        )}
      </div>

      {/* Schedule Message Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white  p-6 rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Schedule Message</h3>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-gray-600 hover:text-gray-800 text-2xl bg-transparent hover:bg-gray-100 rounded p-1"
              >
                ×
              </button>
            </div>

            <div className="space-y-4">
              {/* Patient Selection */}
              <div>
                <label className="block text-sm font-medium mb-2">Patient</label>
                <select
                  value={selectedPatient}
                  onChange={(e) => setSelectedPatient(e.target.value)}
                  className="w-full p-2 border border-gray-300  rounded-lg bg-white "
                >
                  <option value="">Select a patient</option>
                  {patients?.patients?.map((patient: any) => (
                    <option key={patient.id} value={patient.id}>
                      {patient.displayName}
                    </option>
                  ))}
                </select>
              </div>

              {/* Message Type */}
              <div>
                <label className="block text-sm font-medium mb-2">Message Type</label>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="ai_generated"
                      checked={messageType === 'ai_generated'}
                      onChange={(e) => setMessageType(e.target.value as 'ai_generated' | 'custom')}
                      className="mr-2"
                    />
                    <span className="text-sm">AI Generated Reflection</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="custom"
                      checked={messageType === 'custom'}
                      onChange={(e) => setMessageType(e.target.value as 'ai_generated' | 'custom')}
                      className="mr-2"
                    />
                    <span className="text-sm">Custom Message</span>
                  </label>
                </div>
              </div>

              {/* Message Content */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  {messageType === 'ai_generated' ? 'AI Prompt/Reflection' : 'Custom Message'}
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={
                    messageType === 'ai_generated' 
                      ? "Enter a prompt for AI to generate a reflection (e.g., 'Reflect on their recent progress with anxiety management')"
                      : "Enter your custom message"
                  }
                  className="w-full p-2 border border-gray-300  rounded-lg bg-white  h-24 resize-none"
                />
              </div>

              {/* Schedule Date */}
              <div>
                <label className="block text-sm font-medium mb-2">Date</label>
                <input
                  type="date"
                  value={scheduledDate}
                  onChange={(e) => setScheduledDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full p-2 border border-gray-300  rounded-lg bg-white "
                />
              </div>

              {/* Schedule Time */}
              <div>
                <label className="block text-sm font-medium mb-2">Time</label>
                <input
                  type="time"
                  value={scheduledTime}
                  onChange={(e) => setScheduledTime(e.target.value)}
                  className="w-full p-2 border border-gray-300  rounded-lg bg-white "
                />
              </div>

              {/* Action Buttons */}
              <div className="flex space-x-3 pt-4">
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleScheduleMessage}
                  disabled={scheduleMessageMutation.isPending}
                  className="flex-1 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors disabled:opacity-50"
                >
                  {scheduleMessageMutation.isPending ? 'Scheduling...' : 'Schedule Message'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Info Box */}
      <div className="mt-6 p-4 bg-blue-50  rounded-lg">
        <div className="flex items-start space-x-2">
          <div className="text-blue-600  text-lg">💡</div>
          <div className="text-sm text-blue-800 ">
            <strong>Scheduled messages</strong> will be delivered to your patient's chat interface at the specified time. 
            AI-generated messages will be processed by the AI to create personalized reflections based on your prompt.
          </div>
        </div>
      </div>
    </div>
  );
};

export default MessageScheduler; 